/* ============================================================================
 *  TryMeFirst — Build-a-Box checkout guard (v2, no jQuery)
 *
 *  Minis are ₹0 products. The price lives in a hidden "box" product that this
 *  script adds at checkout time. Everything here is client-side; the server-side
 *  backstop is the Shopify Validation Function (see BUNDLE-CHECKOUT-REVIEW.md).
 *
 *  What v2 changes vs v1:
 *   - waits until the cart is actually idle (tracks every /cart/* request) and
 *     reads it twice until stable, instead of a fixed 400 ms sleep
 *   - after adding the box, re-reads and re-verifies; retries on mismatch
 *   - checkout buttons are disabled while any cart request is in flight
 *   - eligibility falls back to real collection membership (product IDs fetched
 *     from Shopify) when the Collections line-item property is missing
 *   - zero-value guard fails CLOSED
 *   - cart API calls retry with backoff on 429/5xx (Shopify throttling under load)
 *   - buttons are type="button" in Liquid, so nothing submits without this script
 * ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ config */
  var ALLOWED_QTYS = [4, 8, 10];
  var BUNDLES = [
    { title: 'Shop Mini',        handle: 'shop-mini',        boxes: { 4: 46217622389027, 8: 46217700475171, 10: 46217713975587 } },
    { title: 'Shop Mini Makeup', handle: 'shop-mini-makeup', boxes: { 4: 47521710539043, 8: 47521714700579, 10: 47521716011299 } }
    /* { title: 'New Collection', handle: 'new-collection', boxes: { 4: ID, 8: ID, 10: ID } }, */
  ];
  var BUTTON_SEL = "#checkout, [name='checkout'], .cart__checkout-button, #fcb-btn";
  var EXPRESS_SEL = '.additional-checkout-buttons, .shopify-payment-button';
  var MAX_SYNC_ATTEMPTS = 3;
  var CART_IDLE_TIMEOUT_MS = 6000;
  var MEMBERSHIP_TTL_MS = 10 * 60 * 1000;

  var BOX_IDS = new Set();
  BUNDLES.forEach(function (b) { Object.keys(b.boxes).forEach(function (q) { BOX_IDS.add(b.boxes[q]); }); });

  function log(action, details) {
    try { console.log('[BAB ' + new Date().toISOString() + '] ' + action, details || ''); } catch (_) {}
  }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /* ---------------------------------------------- in-flight cart tracking */
  var inflight = 0;
  var isCheckoutProcessing = false;
  var isCheckoutTransaction = false;
  var CART_MUTATION_RE = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/;

  function isCartMutation(url) { return CART_MUTATION_RE.test(String(url || '')); }
  function trackStart() { inflight++; applyButtonState(); }
  function trackEnd() { inflight = Math.max(0, inflight - 1); applyButtonState(); }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = (input && input.url) || input;
    if (!isCartMutation(url)) return origFetch.apply(this, arguments);
    trackStart();
    return origFetch.apply(this, arguments).then(
      function (res) { trackEnd(); return res; },
      function (err) { trackEnd(); throw err; }
    );
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartMutation(url)) {
      trackStart();
      this.addEventListener('loadend', trackEnd, { once: true });
    }
    return origOpen.apply(this, arguments);
  };

  function waitForCartIdle(maxMs) {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function poll() {
        if (inflight === 0) return resolve(true);
        if (Date.now() - started > maxMs) { log('cart idle wait timed out', { inflight: inflight }); return resolve(false); }
        setTimeout(poll, 50);
      })();
    });
  }

  /* ---------------------------------------------------------- cart I/O */
  /* Shopify throttles the cart API under load (HTTP 429) and can return 5xx.
     Retry those with backoff (honouring Retry-After) before giving up; a 4xx
     other than 429 is a real error and is not retried. */
  var RETRY_DELAYS_MS = [400, 900, 1800];
  function isRetryable(status) { return status === 429 || status === 408 || status >= 500; }

  async function cartRequest(method, path, body) {
    var opts = { method: method, headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    var lastErr = null;
    for (var attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        var res = await origFetch(path, opts);
        if (res.ok) return await res.json();
        lastErr = new Error(method + ' ' + path + ' -> HTTP ' + res.status);
        if (!isRetryable(res.status)) throw lastErr;
        var retryAfter = parseFloat(res.headers.get('Retry-After'));
        if (attempt < RETRY_DELAYS_MS.length) {
          var delay = isNaN(retryAfter) ? RETRY_DELAYS_MS[attempt] : Math.min(retryAfter * 1000, 5000);
          log('cart request throttled, retrying', { path: path, status: res.status, attempt: attempt + 1, delay: delay });
          await wait(delay);
        }
      } catch (e) {
        lastErr = e;
        if (e && /HTTP \d+/.test(e.message) && !isRetryable(parseInt(e.message.split('HTTP ')[1], 10))) throw e;
        if (attempt < RETRY_DELAYS_MS.length) {
          log('cart request failed, retrying', { path: path, error: e && e.message, attempt: attempt + 1 });
          await wait(RETRY_DELAYS_MS[attempt]);
        }
      }
    }
    throw lastErr;
  }
  function getCart() { return cartRequest('GET', '/cart.js?t=' + Date.now()); }
  function updateCart(updates) {
    if (!updates || !Object.keys(updates).length) return Promise.resolve(null);
    trackStart();
    return cartRequest('POST', '/cart/update.js', { updates: updates }).then(
      function (r) { trackEnd(); return r; }, function (e) { trackEnd(); throw e; });
  }
  function addToCart(items) {
    if (!items || !items.length) return Promise.resolve(null);
    trackStart();
    return cartRequest('POST', '/cart/add.js', { items: items }).then(
      function (r) { trackEnd(); return r; }, function (e) { trackEnd(); throw e; });
  }

  function cartSignature(cart) {
    return cart.items.map(function (i) { return i.key + ':' + i.quantity; }).sort().join('|');
  }

  /* Read the cart only once nothing is in flight, then read again until two
     consecutive reads agree. This is what replaces the old fixed 400 ms sleep. */
  async function readStableCart() {
    await waitForCartIdle(CART_IDLE_TIMEOUT_MS);
    var prev = await getCart();
    for (var i = 0; i < 4; i++) {
      await wait(250);
      await waitForCartIdle(CART_IDLE_TIMEOUT_MS);
      var next = await getCart();
      if (cartSignature(prev) === cartSignature(next)) return next;
      log('cart changed between reads, re-reading', { attempt: i + 1 });
      prev = next;
    }
    return prev;
  }

  /* ---------------------------------------- collection membership fallback */
  var membership = null; // { handle: Set(product_id) }

  async function fetchCollectionProductIds(handle) {
    var ids = [];
    for (var page = 1; page <= 8; page++) {
      var res = await origFetch('/collections/' + handle + '/products.json?limit=250&page=' + page, { headers: { Accept: 'application/json' } });
      if (!res.ok) break;
      var data = await res.json();
      var products = (data && data.products) || [];
      products.forEach(function (p) { ids.push(p.id); });
      if (products.length < 250) break;
    }
    return ids;
  }

  async function loadMembership() {
    if (membership) return membership;
    var KEY = 'bab_membership_v1';
    try {
      var cached = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (cached && Date.now() - cached.at < MEMBERSHIP_TTL_MS) {
        membership = {};
        Object.keys(cached.data).forEach(function (h) { membership[h] = new Set(cached.data[h]); });
        return membership;
      }
    } catch (_) {}
    var result = {}, serial = {};
    for (var i = 0; i < BUNDLES.length; i++) {
      try {
        var ids = await fetchCollectionProductIds(BUNDLES[i].handle);
        result[BUNDLES[i].handle] = new Set(ids); serial[BUNDLES[i].handle] = ids;
      } catch (e) {
        log('membership fetch failed', { handle: BUNDLES[i].handle, error: e && e.message });
        result[BUNDLES[i].handle] = new Set();
      }
    }
    membership = result;
    try { sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), data: serial })); } catch (_) {}
    return membership;
  }

  function parseCollections(prop) {
    if (!prop || typeof prop !== 'string') return [];
    return prop.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /* Returns the bundle a cart line belongs to, or null. Property first, then
     real collection membership, so items added by apps / Shop app still count. */
  function bundleForItem(item) {
    var titles = parseCollections(item.properties && item.properties.Collections);
    for (var i = 0; i < BUNDLES.length; i++) if (titles.indexOf(BUNDLES[i].title) !== -1) return BUNDLES[i];
    if (membership) {
      for (var j = 0; j < BUNDLES.length; j++) {
        var set = membership[BUNDLES[j].handle];
        if (set && set.has(item.product_id)) return BUNDLES[j];
      }
    }
    return null;
  }

  function isFreebie(item) {
    var titles = parseCollections(item.properties && item.properties.Collections);
    return titles.indexOf('Freebies') !== -1 || (item.properties && item.properties._gift);
  }

  function analyse(cart) {
    var counts = {}, boxLines = {}, hasOther = false;
    BUNDLES.forEach(function (b) { counts[b.handle] = 0; boxLines[b.handle] = []; });
    var strayBoxes = [];
    cart.items.forEach(function (item) {
      if (BOX_IDS.has(item.variant_id)) {
        var owner = BUNDLES.find(function (b) { return Object.keys(b.boxes).some(function (q) { return b.boxes[q] === item.variant_id; }); });
        if (owner) boxLines[owner.handle].push(item); else strayBoxes.push(item);
        return;
      }
      var b = bundleForItem(item);
      /* A ₹0 line that is not a box and not a freebie is a mini even if the product
         was left out of the Shop Mini collection — otherwise it rides along free. */
      if (!b && item.final_price === 0 && !isFreebie(item)) b = BUNDLES[0];
      if (b) counts[b.handle] += item.quantity; else hasOther = true;
    });
    var total = BUNDLES.reduce(function (s, b) { return s + counts[b.handle]; }, 0);
    return { counts: counts, boxLines: boxLines, strayBoxes: strayBoxes, hasOther: hasOther, totalEligible: total };
  }

  function quantityMessage(bundle, qty) {
    var higher = ALLOWED_QTYS.filter(function (q) { return q > qty; })[0];
    var lower = ALLOWED_QTYS.filter(function (q) { return q < qty; }).pop();
    var list = ALLOWED_QTYS.join(', ');
    if (higher) { var n = higher - qty; return 'Add ' + n + ' more ' + (n === 1 ? 'item' : 'items') + ' from ' + bundle.title + ' to continue (boxes are ' + list + ' items).'; }
    if (lower)  { var m = qty - lower;  return 'Remove ' + m + ' ' + (m === 1 ? 'item' : 'items') + ' from ' + bundle.title + ' to continue (boxes are ' + list + ' items).'; }
    return 'Boxes are ' + list + ' items. You have ' + qty + '.';
  }

  /* Is the cart's box state exactly what the mini counts require? */
  function boxesMatch(a) {
    if (a.strayBoxes.length) return false;
    for (var i = 0; i < BUNDLES.length; i++) {
      var b = BUNDLES[i], qty = a.counts[b.handle], lines = a.boxLines[b.handle];
      var want = qty ? b.boxes[qty] : null;
      if (!want) { if (lines.length) return false; continue; }
      if (lines.length !== 1) return false;
      if (lines[0].variant_id !== want || lines[0].quantity !== 1) return false;
      if (!(lines[0].final_line_price > 0)) return false;
    }
    return true;
  }

  /* ------------------------------------------------------------ buttons */
  var busyLabel = 'Updating cart…';
  function buttons() { return Array.prototype.slice.call(document.querySelectorAll(BUTTON_SEL)); }

  function rememberLabel(btn) {
    if (btn.dataset.babLabel === undefined) {
      var textEl = btn.querySelector('#fcb-btn-text') || btn;
      btn.dataset.babLabel = textEl.textContent;
    }
  }
  function setLabel(btn, text) {
    var textEl = btn.querySelector('#fcb-btn-text') || btn;
    if (text === null) {
      /* The floating bar re-renders its own label; only restore stock buttons. */
      if (!btn.querySelector('#fcb-btn-text') && btn.dataset.babLabel !== undefined) textEl.textContent = btn.dataset.babLabel;
    } else textEl.textContent = text;
  }

  function applyButtonState() {
    var busy = inflight > 0 || isCheckoutProcessing;
    buttons().forEach(function (btn) {
      if (busy) {
        if (!btn.dataset.babBusy) {
          rememberLabel(btn);
          btn.dataset.babBusy = '1';
          btn.dataset.babWasDisabled = btn.disabled ? '1' : '';
          btn.disabled = true; btn.setAttribute('aria-busy', 'true');
          setLabel(btn, busyLabel);
        }
      } else if (btn.dataset.babBusy) {
        delete btn.dataset.babBusy;
        btn.disabled = btn.dataset.babWasDisabled === '1';
        btn.removeAttribute('aria-busy');
        setLabel(btn, null);
      }
    });
  }

  var expressShouldBeHidden = false;
  function toggleExpress(hide) {
    expressShouldBeHidden = !!hide;
    document.querySelectorAll(EXPRESS_SEL).forEach(function (el) { el.style.display = hide ? 'none' : ''; });
  }
  function enforceExpressHidden() { if (expressShouldBeHidden) toggleExpress(true); }
  [500, 1200, 2500, 4000].forEach(function (ms) { setTimeout(enforceExpressHidden, ms); });

  /* --------------------------------------------- lingering-box cleanup */
  async function removeLingeringBoxes() {
    if (isCheckoutTransaction) return;
    try {
      var cart = await getCart();
      if (isCheckoutTransaction) return;
      var a = analyse(cart), toRemove = {};
      cart.items.forEach(function (i) { if (BOX_IDS.has(i.variant_id)) toRemove[i.key] = 0; });
      toggleExpress(a.totalEligible > 0);
      if (Object.keys(toRemove).length && !isCheckoutTransaction) {
        await updateCart(toRemove);
        log('removed lingering box lines', toRemove);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) { log('lingering-box cleanup failed', { error: e && e.message }); }
  }

  window.addEventListener('pageshow', function (e) {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (e.persisted || (nav && nav.type === 'back_forward')) {
      isCheckoutProcessing = false; applyButtonState();
      removeLingeringBoxes();
    }
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && !isCheckoutTransaction) { isCheckoutProcessing = false; applyButtonState(); }
  });

  /* ------------------------------------------------------- checkout flow */
  async function syncBoxesAndVerify() {
    await loadMembership();
    for (var attempt = 1; attempt <= MAX_SYNC_ATTEMPTS; attempt++) {
      var cart = await readStableCart();
      var a = analyse(cart);
      log('cart analysed', { attempt: attempt, counts: a.counts, total: a.totalEligible, item_count: cart.item_count });

      if (a.totalEligible === 0) {
        var orphan = {};
        cart.items.forEach(function (i) { if (BOX_IDS.has(i.variant_id)) orphan[i.key] = 0; });
        if (Object.keys(orphan).length) { await updateCart(orphan); log('removed orphan boxes', orphan); continue; }
        return { ok: true, cart: cart, bundle: false };
      }

      for (var i = 0; i < BUNDLES.length; i++) {
        var b = BUNDLES[i], qty = a.counts[b.handle];
        if (qty > 0 && !b.boxes[qty]) return { ok: false, reason: quantityMessage(b, qty) };
      }

      if (boxesMatch(a)) return { ok: true, cart: cart, bundle: true };

      /* Box state is wrong: clear every box line, add the right ones, loop to re-verify. */
      var remove = {};
      cart.items.forEach(function (it) { if (BOX_IDS.has(it.variant_id)) remove[it.key] = 0; });
      await updateCart(remove);
      var add = [];
      BUNDLES.forEach(function (bb) { var q = a.counts[bb.handle]; if (q && bb.boxes[q]) add.push({ id: bb.boxes[q], quantity: 1 }); });
      await addToCart(add);
      log('boxes rewritten', { removed: Object.keys(remove).length, added: add });
    }
    return { ok: false, reason: 'Your cart kept changing while we prepared checkout. Please wait a moment and press Checkout again.' };
  }

  async function onCheckoutClick(event) {
    var btn = event.target.closest(BUTTON_SEL);
    if (!btn) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    if (isCheckoutProcessing) { log('ignored: checkout already in progress'); return; }
    isCheckoutProcessing = true; isCheckoutTransaction = true; applyButtonState();

    try {
      var result = await syncBoxesAndVerify();
      if (!result.ok) { alert(result.reason); throw new Error('validation: ' + result.reason); }

      /* Final guard — fail CLOSED. */
      var finalCart = await getCart();
      var fa = analyse(finalCart);
      var valid = fa.totalEligible === 0 ? !finalCart.items.some(function (i) { return BOX_IDS.has(i.variant_id); }) : boxesMatch(fa);
      if (!valid || (finalCart.item_count > 0 && finalCart.total_price === 0 && fa.totalEligible > 0)) {
        alert('Something changed in your cart. Please review it and press Checkout again.');
        throw new Error('final verification failed');
      }
      toggleExpress(fa.totalEligible > 0);
      log('verified, redirecting', { total_price: finalCart.total_price, item_count: finalCart.item_count });
      window.location.href = '/checkout';
    } catch (err) {
      log('checkout aborted', { error: err && err.message });
      /* Validation errors already alerted. Anything else is a cart-API failure
         the customer must hear about, or the button looks broken. */
      if (!(err && /^validation:|^final verification/.test(err.message || ''))) {
        alert('We could not update your cart just now (the store is busy). Please wait a moment and press Checkout again.');
      }
      isCheckoutProcessing = false; applyButtonState();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      isCheckoutTransaction = false;
    }
  }

  /* Capture phase so this runs before any other click handler on the button. */
  document.addEventListener('click', onCheckoutClick, true);

  /* Enter inside the cart form must not submit to /cart?checkout either. */
  document.addEventListener('submit', function (e) {
    var sub = e.submitter, form = e.target;
    if ((sub && sub.matches && sub.matches(BUTTON_SEL)) || (form && form.classList && form.classList.contains('fcb-form'))) e.preventDefault();
  }, true);

  /* ------------------------------------------------------------- boot */
  applyButtonState();
  removeLingeringBoxes();
  loadMembership().then(function (m) {
    var sizes = {}; Object.keys(m).forEach(function (h) { sizes[h] = m[h].size; });
    log('membership loaded', sizes);
  });

  window.BuildABox = { analyse: analyse, getCart: getCart, readStableCart: readStableCart, inflight: function () { return inflight; } };
})();
